import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import ServiceList from "./ServiceList";
import ServiceForm from "./ServiceForm";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import BackButton from "../../../../components/Button/BackButton";
import { toast } from "../../../../components/Toast/toast";

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await api.get("/admin-services");
      setServices(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch services");
    } finally {
      setLoading(false);
    }
  };

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (formData) => {
    try {
      if (action === "edit" && editId) {
        const res = await api.put(`/admin-services/${editId}`, formData);
        setServices(
          services.map((s) => (String(s.id) === String(editId) ? res.data.data : s))
        );
      } else {
        const res = await api.post("/admin-services", formData);
        setServices((prev) => [...prev, res.data.data]);
      }
      setAction(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save service");
    }
  };

  const handleDelete = async () => {
    if (!serviceToDelete) return;
    try {
      await api.delete(`/admin-services/${serviceToDelete}`);
      setServices(services.filter((s) => s.id !== serviceToDelete));
      setServiceToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete service");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const inForm = action === "add" || (action === "edit" && editId);
  const editingService =
    action === "edit" && editId
      ? services.find((s) => String(s.id) === String(editId))
      : null;

  return (
    <>
      <div className="md:w-1/2">
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton
              onClick={inForm ? () => setAction(null) : undefined}
            />
            <h1 className="text-2xl font-bold text-gray-900">Services</h1>
          </div>
          {!inForm && (
            <button
              onClick={() => setAction("add")}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
            >
              Add Service
            </button>
          )}
        </div>

        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

        {inForm ? (
          <ServiceForm
            serviceToEdit={editingService}
            onSave={handleSave}
            onCancel={() => setAction(null)}
          />
        ) : (
          <ServiceList
            services={services}
            onEdit={(service) => setAction("edit", service.id)}
            onDelete={(id) => setServiceToDelete(id)}
          />
        )}
      </div>

      {serviceToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this service? This action cannot be undone."
          onCancel={() => setServiceToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
          confirmColor="bg-red-600"
          confirmHoverColor="hover:bg-red-700"
        />
      )}
    </>
  );
};

export default ServicesPage;
